/**
 * "Your components, actions & data" — Widget section (ADR-0074).
 * The host-app extension surface: registerComponent / registerProviders /
 * registerAction / registerDataSource / setContext, plus the web-components
 * bridge. Reconciled from apps/widget/src/main.ts (the `SyncanixGlobal`
 * contract and its JSDoc examples) and packages/web-components.
 */
import type { DocPageContent } from '../types';

const COMPONENT = `import { z } from 'zod';
import { OrderCard } from './OrderCard';

const OrderCardProps = z.object({ orderId: z.string() });

window.syncanix?.registerComponent('OrderCard', OrderCard, OrderCardProps, {
  description: 'Shows an order summary',
  propDescriptions: { orderId: 'Which order to show' },
});`;

const PROVIDERS = `window.syncanix?.registerProviders(({ children }) => (
  <ApolloProvider client={apolloClient}>
    <ThemeProvider theme={theme}>{children}</ThemeProvider>
  </ApolloProvider>
));`;

const ACTIONS = `window.syncanix?.registerAction('refetch_orders', () => ordersQuery.refetch(), {
  description: 'Reload the orders table with the latest data',
});

window.syncanix?.registerAction('go_to_invoice', ({ id }) => navigate(\`/invoices/\${id}\`), {
  parameters: z.object({ id: z.string() }),
  parameterDescriptions: { id: 'The invoice id to open' },
  description: 'Navigate to a specific invoice',
});`;

const DATA_SOURCE = `window.syncanix?.registerDataSource(
  'open_orders',
  async (params) => ({ rows: await api.orders({ status: 'open', ...params }) }),
  { description: 'Current open orders', pollMs: 30000 },
);`;

const CONTEXT = `window.syncanix?.setContext({
  user: { id: 'u_1', plan: 'pro' },
  page: { path: '/checkout' },
});`;

const WEB_COMPONENT = `import { defineSyncanixComponent } from '@syncanix/web-components';
import { z } from 'zod';

const StatusBadge = defineSyncanixComponent(
  'status-badge',
  StatusBadgeElement, // your HTMLElement subclass
  z.object({ label: z.string(), tone: z.string() }),
);

window.syncanix?.registerComponent('status-badge', StatusBadge, StatusBadge.propsSchema);`;

export const WIDGET_EXTENDING_PAGE: DocPageContent = {
  slug: 'widget/extending',
  titleKey: 'docs.widget.extending.title',
  descriptionKey: 'docs.widget.extending.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.widget.extending.lead' },
    {
      kind: 'callout',
      tone: 'important',
      titleKey: 'docs.widget.extending.safety.title',
      textKey: 'docs.widget.extending.safety.body',
    },

    {
      kind: 'heading',
      level: 2,
      id: 'components',
      textKey: 'docs.widget.extending.components.heading',
    },
    { kind: 'paragraph', textKey: 'docs.widget.extending.components.body' },
    { kind: 'code', language: 'tsx', code: COMPONENT },
    { kind: 'paragraph', textKey: 'docs.widget.extending.components.validation' },

    {
      kind: 'heading',
      level: 3,
      id: 'providers',
      textKey: 'docs.widget.extending.providers.heading',
    },
    { kind: 'paragraph', textKey: 'docs.widget.extending.providers.body' },
    { kind: 'code', language: 'tsx', code: PROVIDERS },

    { kind: 'heading', level: 2, id: 'actions', textKey: 'docs.widget.extending.actions.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.extending.actions.body' },
    { kind: 'code', language: 'tsx', code: ACTIONS },
    { kind: 'paragraph', textKey: 'docs.widget.extending.actions.scoping' },

    { kind: 'heading', level: 2, id: 'data', textKey: 'docs.widget.extending.data.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.extending.data.body' },
    { kind: 'code', language: 'tsx', code: DATA_SOURCE },

    { kind: 'heading', level: 2, id: 'context', textKey: 'docs.widget.extending.context.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.extending.context.body' },
    { kind: 'code', language: 'tsx', code: CONTEXT },
    {
      kind: 'callout',
      tone: 'caution',
      titleKey: 'docs.widget.extending.context.caution.title',
      textKey: 'docs.widget.extending.context.caution.body',
    },

    {
      kind: 'heading',
      level: 2,
      id: 'web-components',
      textKey: 'docs.widget.extending.webComponents.heading',
    },
    { kind: 'paragraph', textKey: 'docs.widget.extending.webComponents.body' },
    { kind: 'code', language: 'bash', code: 'npm install @syncanix/web-components' },
    { kind: 'code', language: 'tsx', code: WEB_COMPONENT },

    {
      kind: 'heading',
      level: 2,
      id: 'auto-detect',
      textKey: 'docs.widget.extending.autoDetect.heading',
    },
    { kind: 'paragraph', textKey: 'docs.widget.extending.autoDetect.body' },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.widget.extending.next.api.title',
          textKey: 'docs.widget.extending.next.api.text',
          to: { docSlug: 'widget/runtime-api' },
        },
        {
          titleKey: 'docs.widget.extending.next.rendering.title',
          textKey: 'docs.widget.extending.next.rendering.text',
          to: { docSlug: 'rendering' },
        },
      ],
    },
  ],
};
